import { useState } from "react";
import { toast } from "sonner";
import { Section, Eyebrow } from "@/components/Section";
import { api } from "@/lib/api";
import { Clock, MapPin, Phone, Loader2, ArrowUpRight, CheckCircle2 } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const valid =
    form.name.trim().length > 1 &&
    /\S+@\S+\.\S+/.test(form.email) &&
    form.message.trim().length > 3;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message received. I'll reply within two business days.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't send your message. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Couldn't send your message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Section testId="contact-hero" className="!pt-24 !pb-10">
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <Eyebrow>Contact</Eyebrow>
            <h1 className="font-serif-display text-4xl sm:text-6xl lg:text-7xl mt-6 text-[#2E3630] leading-[1.02]">
              Reach out. I read every message.
            </h1>
            <p className="font-body mt-8 text-lg text-[#5F6B62] max-w-2xl leading-relaxed">
              Questions about availability, insurance, fit, or anything else?
              Send a note. I usually respond within two business days.
            </p>
          </div>
        </div>
      </Section>

      <Section testId="contact-grid" className="!pt-6 !pb-32">
        <div className="grid lg:grid-cols-12 gap-14">
          <aside className="lg:col-span-5 space-y-10" data-testid="contact-info">
            <div>
              <Eyebrow>Practice</Eyebrow>
              <ul className="mt-6 space-y-5 font-body text-[#2E3630]">
                <li className="flex items-start gap-4">
                  <span className="h-10 w-10 inline-flex items-center justify-center rounded-full bg-[#F0EDE6] text-[#6B8E7B] shrink-0">
                    <MapPin size={18} />
                  </span>
                  <div>
                    <p className="eyebrow text-[10px]">Location</p>
                    <p className="font-serif-display text-xl sm:text-2xl mt-1 leading-tight text-[#2E3630]">
                      2560 Sheridan Blvd.<br />
                      Denver, CO 80214
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="h-10 w-10 inline-flex items-center justify-center rounded-full bg-[#F0EDE6] text-[#6B8E7B] shrink-0">
                    <Phone size={18} />
                  </span>
                  <div>
                    <p className="eyebrow text-[10px]">Phone</p>
                    <a href="tel:+17204774692" className="font-serif-display text-xl sm:text-2xl mt-1 leading-tight hover:text-[#6B8E7B] transition-colors block">(720) 477-4692</a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="h-10 w-10 inline-flex items-center justify-center rounded-full bg-[#F0EDE6] text-[#6B8E7B] shrink-0">
                    <Clock size={18} />
                  </span>
                  <div>
                    <p className="eyebrow text-[10px]">Hours</p>
                    <p className="font-body text-base mt-1 text-[#5F6B62] leading-relaxed">
                      Monday – Thursday · 11 AM – 8 PM<br />
                      Friday & Saturday · By appointment only<br />
                      Sunday · Closed
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </aside>

          <div className="lg:col-span-7" data-testid="contact-form-wrapper">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-white rounded-[2rem] border border-[#E8E5DF] p-8 sm:p-12"
            >
              <Eyebrow>Send a message</Eyebrow>

              {sent && (
                <div className="mt-6 rounded-2xl bg-[#F0EDE6] p-5 flex items-start gap-3" data-testid="contact-success">
                  <CheckCircle2 size={20} className="text-[#6B8E7B] mt-0.5" />
                  <p className="font-body text-sm text-[#2E3630]">
                    Thank you — your message is in. I'll reply within two business days.
                  </p>
                </div>
              )}

              <div className="mt-8 grid sm:grid-cols-2 gap-6">
                <label className="block">
                  <span className="eyebrow text-[10px]">Full name</span>
                  <input
                    type="text"
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={(e) => setField("name", e.target.value)}
                    placeholder="Alex Rivera"
                    className="input-underline"
                    required
                  />
                </label>
                <label className="block">
                  <span className="eyebrow text-[10px]">Email</span>
                  <input
                    type="email"
                    data-testid="contact-input-email"
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                    placeholder="alex@example.com"
                    className="input-underline"
                    required
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="eyebrow text-[10px]">Phone <span className="lowercase tracking-normal text-[#9AA39C]">(optional)</span></span>
                  <input
                    type="tel"
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={(e) => setField("phone", e.target.value)}
                    placeholder="(720) 555-0100"
                    className="input-underline"
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="eyebrow text-[10px]">Message</span>
                  <textarea
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={(e) => setField("message", e.target.value)}
                    rows={6}
                    placeholder="A few sentences about what brings you here."
                    className="input-underline resize-none"
                    required
                  />
                </label>
              </div>

              <div className="mt-10 pt-8 border-t border-[#E8E5DF] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="font-body text-xs text-[#9AA39C] max-w-md leading-relaxed">
                  This form is not for emergencies. If you're in crisis, please call or text 988, or 911.
                </p>
                <button
                  type="submit"
                  data-testid="contact-form-submit-button"
                  disabled={!valid || submitting}
                  className="btn-pill btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <><Loader2 size={16} className="animate-spin" /> Sending…</>
                  ) : (
                    <>Send message <ArrowUpRight size={16} /></>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Section>
    </>
  );
}
